import React from "react";
import { useTranslation } from "react-i18next";
import { FiMapPin } from "react-icons/fi";
import { clients, logoClients } from "../content/clients";
import type { Client } from "../content/clients";
import { combinedYears } from "../content/team";
import "../styles/proof.css";

/**
 * Trimmed, 2× monochrome-ready logos produced by WP-B in public/assets/clients/.
 * `ratio` is width / height of the processed asset, so the <img> reserves its box
 * before load. If the processed file is missing, onError falls back to `client.logo`.
 */
const PROCESSED: Record<Client["id"], { src: string; ratio: number }> = {
  hirotec: { src: "/assets/clients/hirotec.png", ratio: 68 / 88 },
  mane: { src: "/assets/clients/mane.png", ratio: 173 / 40 },
};

const ClientLogo: React.FC<{ client: Client }> = ({ client }) => {
  const processed = PROCESSED[client.id];
  const h = client.logoHeight;
  return (
    <img
      className="proof-logo"
      src={processed?.src ?? client.logo}
      alt={client.name}
      height={h}
      width={Math.round(h * (processed?.ratio ?? 2))}
      style={{ height: h }}
      loading="lazy"
      decoding="async"
      onError={(e) => {
        const img = e.currentTarget;
        if (img.dataset.fallback) return;
        img.dataset.fallback = "1";
        img.src = client.logo;
      }}
    />
  );
};

/**
 * Proof strip under the hero (spec c.7 §3, COO decision 3): consented client
 * logos (Hirotec, Mane), the combined-years figure from team.ts, and markets.
 * With no consented logos it shows the facts only, with no layout change.
 */
const ProofStrip: React.FC = () => {
  const { t } = useTranslation();
  const logos = logoClients(clients);
  const years = combinedYears();

  return (
    <section className="proof" aria-label={t("proof.ariaLabel")}>
      <div className="container">
        <div className="proof-band">
          {logos.length > 0 && (
            <div className="proof-clients">
              <p className="t-small proof-label">{t("proof.clientsLabel")}</p>
              <ul className="proof-logos">
                {logos.map((c) => (
                  <li key={c.id}>
                    <ClientLogo client={c} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="proof-facts">
            {years > 0 && (
              <li className="proof-fact">
                <strong className="proof-num">{years}+</strong> {t("proof.yearsLabel")}
              </li>
            )}
            <li className="proof-fact">
              <FiMapPin className="proof-icon" aria-hidden="true" focusable="false" />
              <span className="visually-hidden">{t("proof.marketsLabel")}: </span>
              {t("proof.markets")}
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProofStrip;
